import { useEffect, useRef, useState } from "react";
import { Refresh } from "iconoir-react";

type Props = {
  disabled: boolean;
  onReset: () => void;
};

const CONFIRM_TIMEOUT_MS = 5000;

/** Reset throws away the grid, the clock and the saved game at once. It is used at
    most once a game, so it lives below the board rather than in the working row —
    Undo, Redo and Reset are three near-identical curved arrows once the labels drop
    away on a phone, and only one of them is expensive to hit by mistake. */
export function ResetButton({ disabled, onReset }: Props) {
  const [confirming, setConfirming] = useState(false);
  const timer = useRef<number | undefined>(undefined);

  useEffect(() => () => window.clearTimeout(timer.current), []);

  // It asks first, and stops asking if the player walks away from the question.
  function armReset(): void {
    setConfirming(true);
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setConfirming(false), CONFIRM_TIMEOUT_MS);
  }

  function confirmReset(): void {
    window.clearTimeout(timer.current);
    setConfirming(false);
    onReset();
  }

  return (
    <div className="reset-control">
      {/* The button stays in flow while the prompt floats over it, so the footnote
          keeps exactly the same height in both states. */}
      <button
        className={confirming ? "text-button is-hidden" : "text-button"}
        type="button"
        aria-label="Reset puzzle"
        disabled={confirming || disabled}
        onClick={armReset}
      >
        <Refresh width={18} height={18} strokeWidth={2} aria-hidden="true" /> <span className="control-label">Reset</span>
      </button>
      {confirming && (
        <span className="reset-confirm" role="alert">
          <span>Clear the whole grid?</span>
          <button className="text-button is-danger" type="button" onClick={confirmReset} autoFocus>Reset</button>
          <button className="text-button" type="button" onClick={() => setConfirming(false)}>Keep</button>
        </span>
      )}
    </div>
  );
}
